import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MentionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/mention/mention.component';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { IMentionAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TStatusBadge, TStatusBadgeColorScheme } from '@shared/components/status-badge/status-badge.interface';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';
import { IMentionPostAnalyticsTableData } from './mention-post-analytics.interface';

@Component({
  imports: [
    TranslateModule,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MentionComponent,
    LowerCasePipe,
    MatMenuModule,
    DotsSpinnerComponent,
    IconComponent,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-mention-post-analytics',
  templateUrl: './mention-post-analytics.component.html',
})
export class MentionPostAnalyticsComponent extends TextPostAnalyticsBaseDirective<
  IMentionPostAnalyticsTableData,
  IMentionAnalytics
> {
  public override readonly deliverableType = EDeliverableType.mention;
  public override readonly displayedColumns: string[] = ['text', 'actions'];
  public override readonly kpiCardData = null;

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.mentions?.find((m) => m.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly contentData = computed(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    if (!socialContent) {
      return null;
    }

    if ('instagramPostData' in socialContent) {
      return socialContent.instagramPostData;
    }

    if ('instagramReelData' in socialContent) {
      return socialContent.instagramReelData;
    }

    if ('xData' in socialContent) {
      return socialContent.xData;
    }

    if ('tiktokData' in socialContent) {
      return socialContent.tiktokData;
    }

    return null;
  });

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  constructor() {
    super();
  }

  protected override setKeywordCounts(): void {
    const mention = this.executionAnalyticsWithFindings()?.ctaAnalytics?.mentions?.find(
      (m) => m.deliverableId === this.deliverableId(),
    );

    const keywords = mention?.keywords?.map((k) => k.keyword) ?? [];
    const mentions = this.contentData()?.mentions ?? [];
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, mentions);

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override prepareTableData(): void {
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: IMentionPostAnalyticsTableData[] = visualizers.map((visualizer) => ({
      ...visualizer,
      text: visualizer.findings[0].text,
    }));

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
  }

  protected override setKeywordCardList(): void {
    const mention = this.executionAnalyticsWithFindings()?.ctaAnalytics?.mentions?.find(
      (m) => m.deliverableId === this.deliverableId(),
    );

    if (mention?.keywords && mention.keywords.length) {
      const kpiCardList = mention.keywords.map((k) => {
        const badgeStatus: TStatusBadge = this.keywordCounts[k.keyword] ? 'found' : 'notFound';
        const badgeColorScheme: TStatusBadgeColorScheme = 'deliverable';

        return {
          badgeColorScheme,
          badgeStatus,
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
